import React, { useState } from 'react';
import './styles/App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './features/shared/contexts/LanguageContext';
import { ThemeProvider } from './features/shared/contexts/ThemeContext';
import { AuthProvider, useAuth } from './features/auth/contexts/AuthContext';
import { CompanyProvider } from './features/company/contexts/CompanyContext';
import { ToastProvider } from './features/shared/contexts/ToastContext';
import { LoadingProvider } from './features/shared/contexts/LoadingContext';
import Sidebar from './features/shared/components/Sidebar';
import ProjectsOverview from './features/shared/components/ProjectsOverview';
import TranslatorProject from './features/translator/components/TranslatorProject';
import TranslatorPage from './features/translator/components/TranslatorPage';
import LoadingAnimation from './features/shared/components/LoadingAnimation';
import ToastContainer from './features/shared/components/ToastContainer';
import AdminPanel from './features/admin/components/AdminPanel';
import FRAdminPanel from './features/admin/components/FRAdminPanel';
import FRUpdatePage from './features/fr-update/components/FRUpdatePage';

// Settings bileşeni
const SettingsPage: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div>
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">{t('settings')}</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">Customize your experience</p>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* General Settings */}
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">General</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Language</label>
                <select className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Theme</label>
                <select className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Notification Settings */}
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">Email notifications</span>
                <input type="checkbox" className="w-5 h-5 text-[#02c2ff] rounded focus:ring-[#02c2ff]" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">Push notifications</span>
                <input type="checkbox" className="w-5 h-5 text-[#02c2ff] rounded focus:ring-[#02c2ff]" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-300">Project updates</span>
                <input type="checkbox" className="w-5 h-5 text-[#02c2ff] rounded focus:ring-[#02c2ff]" defaultChecked />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 flex justify-center">
          <button className="bg-[#02c2ff] hover:bg-[#0199cc] text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

// Protected Route Component for Admin Panel
const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canAccessAdminPanel } = useAuth();
  
  if (!canAccessAdminPanel()) {
    return <Navigate to="/home" replace />;
  }
  
  return <>{children}</>;
};

// Protected Route Component for Translate Admin Panel
const ProtectedTranslateAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canAccessTranslateAdminPanel } = useAuth();
  
  if (!canAccessTranslateAdminPanel()) {
    return <Navigate to="/home" replace />;
  }
  
  return <>{children}</>;
};

// Protected Route Component for FR Update Admin Panel
const ProtectedFRUpdateAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canAccessFRUpdateAdminPanel } = useAuth();
  
  if (!canAccessFRUpdateAdminPanel()) {
    return <Navigate to="/home" replace />;
  }
  
  return <>{children}</>;
};

const MainContent: React.FC<{ onProjectSelect: (project: string) => void; isSidebarHovered: boolean }> = ({ onProjectSelect, isSidebarHovered }) => {
  const location = useLocation();
  const isFRUpdatePage = location.pathname.startsWith('/fr-update');
  
  return (
    <div className="min-h-screen">
      {/* Ana içerik container'ı - sidebar ile aynı boyut ve konumlandırma */}
      <div
        className="app-main-content fixed bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 transition-all duration-300 z-10 p-8 overflow-y-auto thin-scrollbar"
        style={{ '--desktop-content-left': isSidebarHovered ? '368px' : '128px' } as React.CSSProperties}
      >
        <div className={isFRUpdatePage ? 'h-full max-w-7xl mx-auto' : 'max-w-7xl mx-auto'}>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<ProjectsOverview onProjectSelect={onProjectSelect} />} />
            <Route path="/translator" element={<TranslatorProject />} />
            <Route path="/translate" element={<TranslatorPage />} />
            <Route path="/fr-update" element={<FRUpdatePage />} />
            <Route path="/fr-update/:company_id" element={<FRUpdatePage />} />
            <Route path="/fr-update/:company_id/document-upload" element={<FRUpdatePage />} />
            <Route path="/fr-update/:company_id/documents" element={<FRUpdatePage />} />
            <Route path="/fr-update/:company_id/documents/:document_id/edit" element={<FRUpdatePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminPanel />
              </ProtectedAdminRoute>
            } />
            <Route path="/admin/translate" element={
              <ProtectedTranslateAdminRoute>
                <AdminPanel />
              </ProtectedTranslateAdminRoute>
            } />
            <Route path="/admin/fr-update" element={
              <ProtectedFRUpdateAdminRoute>
                <FRAdminPanel />
              </ProtectedFRUpdateAdminRoute>
            } />
            <Route path="*" element={<div>Page not found</div>} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [showLoading, setShowLoading] = useState(false); // Start with false
  const location = useLocation();
  
  const { isAuthenticated, isLoading, logout } = useAuth();

  // Handle authentication state changes
  React.useEffect(() => {
    if (isLoading) {
      // Still checking authentication, don't show anything yet
      return;
    }
    
    if (isAuthenticated) {
      // User is authenticated, show main app
      setShowLoading(false);
    } else {
      // User is not authenticated, show login animation
      setShowLoading(true);
    }
  }, [isAuthenticated, isLoading]);

  const handleProjectSelect = (project: string) => {
    // Router kullanarak navigasyon yapacağız
  };

  const handleLoadingComplete = () => {
    setShowLoading(false);
    // Login tamamlandığında sidebar hover state'ini sıfırla
    setIsSidebarHovered(false);
  };

  const handleLogout = () => {
    logout();
    setShowLoading(true);
    // Logout sırasında da sidebar hover state'ini sıfırla
    setIsSidebarHovered(false);
  };

  // Show simple loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show loading animation only if explicitly needed (not authenticated and not loading)
  if (showLoading && !isAuthenticated && !isLoading) {
    return <LoadingAnimation onComplete={handleLoadingComplete} />;
  }

  // Show main application if authenticated and not loading
  if (isAuthenticated && !isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
        <Sidebar 
          onHoverChange={setIsSidebarHovered}
          onLogout={handleLogout}
        />
        <MainContent onProjectSelect={handleProjectSelect} isSidebarHovered={isSidebarHovered} />
      </div>
    );
  }

  // Fallback: if we get here, something went wrong, show loading
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CompanyProvider>
            <ToastProvider>
              <LoadingProvider>
                <Router>
                  <AppContent />
                  <ToastContainer />
                </Router>
              </LoadingProvider>
            </ToastProvider>
          </CompanyProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
